/**
 * Answer-reveal row shared by the web SingleSelect and MultiSelect option
 * cards: a decorative icon and the caller's feedback label.
 */

/// <reference lib="dom" />

import React from 'react';
import { Icon } from '../Icon/Icon.web';
import {
  optionFeedbackPresentation,
  FEEDBACK_ICON_SIZE,
  FEEDBACK_ROW_GAP,
  FEEDBACK_LABEL_TYPOGRAPHY,
  type SelectOption,
} from './SingleSelect';

interface OptionFeedbackRowProps {
  option: SelectOption;
  'data-testid'?: string;
}

/**
 * The icon + label row under a revealed option. The option's accessible name
 * already includes the label, so the row is visual only and the icon is
 * decorative.
 */
export function OptionFeedbackRow({ option, 'data-testid': testId }: OptionFeedbackRowProps) {
  if (option.feedback === undefined || !option.feedbackLabel) return null;
  const { iconName, toneColor } = optionFeedbackPresentation(option.feedback);
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: FEEDBACK_ROW_GAP,
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: FEEDBACK_LABEL_TYPOGRAPHY.fontFamily,
    fontWeight: FEEDBACK_LABEL_TYPOGRAPHY.fontWeight,
    fontSize: FEEDBACK_LABEL_TYPOGRAPHY.fontSize,
    lineHeight: `${FEEDBACK_LABEL_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: FEEDBACK_LABEL_TYPOGRAPHY.letterSpacing,
    color: toneColor,
  };
  return (
    <span style={rowStyle} data-testid={testId}>
      <span aria-hidden="true" style={{ display: 'inline-flex' }}>
        <Icon name={iconName} size={FEEDBACK_ICON_SIZE} color={toneColor} />
      </span>
      <span style={labelStyle}>{option.feedbackLabel}</span>
    </span>
  );
}
